/**
 * Client-side handler for the server's `persistence-error` broadcast.
 *
 * After the per-note Y.Doc refactor, each note persists to its own
 * `(room_id, doc_key)` row in `deck_docs_v2`/`sg_docs_v2`. Cloudflare
 * DO SQL still caps each row value at ~2 MiB, so a single oversized
 * note will trip `SQLITE_TOOBIG` for ITS row only — other notes keep
 * persisting. The server broadcasts a `persistence-error` envelope on
 * every ROOT connection naming the offending `docKey`, so the user
 * sees a toast even if that note's editor is on a different tab. (Per-
 * note connections are pure binary Yjs sockets and don't carry
 * `__YPS:` envelopes.)
 *
 * Server payload shape (see `party/framework/sync-yserver.ts → onPersistError`):
 *   { type: "persistence-error",
 *     kind: "sqlite-too-big" | "unknown",
 *     stateBytes: number,
 *     room: string,
 *     doKind: string,
 *     docKey: string,        // "__root" | "note\x01<deckId>\x01<noteId>" | …
 *     message: string }
 */

import * as Sentry from '@sentry/react';

import { emitSharedToast } from './toast-sink';

type PersistenceErrorMsg = {
  type: 'persistence-error';
  kind?: 'sqlite-too-big' | 'unknown';
  stateBytes?: number;
  room?: string;
  doKind?: string;
  /** Identifies WHICH doc inside the room hit the cap. Added with the
   *  per-note refactor; absent on legacy session payloads. */
  docKey?: string;
  message?: string;
};

let lastShownAt = 0;
const SUPPRESS_WINDOW_MS = 30_000;

export function isPersistenceErrorMessage(msg: unknown): msg is PersistenceErrorMsg {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    (msg as { type?: string }).type === 'persistence-error'
  );
}

export function showPersistenceErrorToast(msg: PersistenceErrorMsg): void {
  const now = Date.now();
  if (now - lastShownAt < SUPPRESS_WINDOW_MS) {
    // Avoid stacking dozens of toasts when the debounced save retries.
    return;
  }
  lastShownAt = now;

  // Including docKey in the toast id means different failing notes
  // get distinct toasts (the in-memory suppression window above still
  // throttles total toast volume).
  const toastId = `persistence-error-${msg.room ?? 'unknown'}-${msg.docKey ?? '*'}`;

  // DS-Toast shape per the approved catalog copy (app-sync-persist-*). English
  // only — shared non-React code with no i18n runtime.
  //
  // Both failure kinds ship the SAME copy: from the user's side "we couldn't
  // save and you should talk to us" is the whole actionable message, and the
  // diagnostics that used to sit in the description (which doc, how many MB)
  // are only useful to support — they ride the Sentry extras below instead.
  // The size failure holds on screen longer because it never self-resolves.
  emitSharedToast('critical', {
    title: "Couldn't save changes",
    description: 'Recent edits may be lost. Contact support.',
    icon: 'alert',
    duration: msg.kind === 'sqlite-too-big' ? 15_000 : 12_000,
    id: toastId,
  });

  Sentry.captureMessage('persistence-error received from DO', {
    level: 'error',
    extra: {
      kind: msg.kind,
      stateBytes: msg.stateBytes,
      room: msg.room,
      doKind: msg.doKind,
      docKey: msg.docKey,
      message: msg.message,
    },
  });

  console.error('[persistence-error]', msg);
}
